import { Schema } from "effect";

export const WorkflowStepKind = /* @__PURE__ */ (() => Schema.Literals(["do", "sleep", "sleepUntil", "waitForEvent"]))();
export type WorkflowStepKind = typeof WorkflowStepKind.Type;

export const WorkflowSourceStep = /* @__PURE__ */ (() => Schema.Struct({
  id: Schema.String.pipe(Schema.mutableKey),
  kind: WorkflowStepKind.pipe(Schema.mutableKey),
  name: Schema.String.pipe(Schema.mutableKey),
  line: Schema.Number.pipe(Schema.mutableKey),
  parallelGroup: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
}))();
export type WorkflowSourceStep = typeof WorkflowSourceStep.Type;

export const DetectedWorkflowDefinition = /* @__PURE__ */ (() => Schema.Struct({
  className: Schema.String.pipe(Schema.mutableKey),
  file: Schema.String.pipe(Schema.mutableKey),
  steps: Schema.Array(WorkflowSourceStep).pipe(Schema.mutable, Schema.mutableKey),
}))();
export type DetectedWorkflowDefinition = typeof DetectedWorkflowDefinition.Type;

/** Tokens after which `/` begins a regex literal rather than division. */
const REGEX_PREFIX_TOKENS = new Set([
  "(",
  "[",
  "{",
  ",",
  ";",
  ":",
  "=",
  "=>",
  "?",
  "!",
  "+",
  "-",
  "*",
  "%",
  "&",
  "|",
  "^",
  "~",
  "<",
  ">",
  "return",
  "throw",
  "case",
  "delete",
  "typeof",
  "void",
  "yield",
  "await",
  "else",
  "do",
  "in",
  "of",
  "new",
  "instanceof"
]);

function previousCodeToken(source: string, beforeIndex: number): string {
  let index = beforeIndex - 1;
  while (index >= 0 && /\s/.test(source[index]!)) index -= 1;
  if (index < 0) return "";

  const char = source[index]!;
  if (char === ">" && source[index - 1] === "=") return "=>";
  if ("()[]{},;:?!=+-*%&|^~<>".includes(char)) return char;

  let start = index;
  while (start >= 0 && /[\w$]/.test(source[start]!)) start -= 1;
  return source.slice(start + 1, index + 1);
}

/**
 * Find the matching closer for `{`/`[` at openIndex. Skips strings, comments,
 * and regex literals so a `}` inside `/}/` cannot close a class body early.
 */
function matchingBrace(source: string, openIndex: number): number {
  let depth = 0;
  let quote: string | null = null;
  let escaped = false;
  let lineComment = false;
  let blockComment = false;
  for (let index = openIndex; index < source.length; index++) {
    const char = source[index]!;
    const next = source[index + 1];
    if (lineComment) {
      if (char === "\n") lineComment = false;
      continue;
    }
    if (blockComment) {
      if (char === "*" && next === "/") {
        blockComment = false;
        index++;
      }
      continue;
    }
    if (quote) {
      if (escaped) escaped = false;
      else if (char === "\\") escaped = true;
      else if (char === quote) quote = null;
      continue;
    }
    if (char === "/" && next === "/") {
      lineComment = true;
      index++;
      continue;
    }
    if (char === "/" && next === "*") {
      blockComment = true;
      index++;
      continue;
    }
    if (
      char === "/" &&
      REGEX_PREFIX_TOKENS.has(previousCodeToken(source, index))
    ) {
      // Scan the regex body; `}` / `]` inside must not affect brace depth.
      let characterClass = false;
      for (index += 1; index < source.length; index += 1) {
        const value = source[index]!;
        if (value === "\\") {
          index += 1;
          continue;
        }
        if (value === "[") characterClass = true;
        else if (value === "]") characterClass = false;
        else if (value === "/" && !characterClass) {
          while (/[A-Za-z]/.test(source[index + 1] ?? "")) index += 1;
          break;
        }
      }
      continue;
    }
    if (char === '"' || char === "'" || char === "`") {
      quote = char;
      continue;
    }
    if (char === "{" || char === "[") depth++;
    if (char === "}" || char === "]") {
      depth--;
      if (depth === 0) return index;
    }
  }
  return source.length - 1;
}

function parallelRanges(source: string): Array<[number, number, number]> {
  const ranges: Array<[number, number, number]> = [];
  const pattern = /Promise\s*\.\s*all\s*\(\s*\[/g;
  for (const match of source.matchAll(pattern)) {
    const open = source.indexOf("[", match.index ?? 0);
    if (open < 0) continue;
    ranges.push([open, matchingBrace(source, open), ranges.length + 1]);
  }
  return ranges;
}

/**
 * Collect local identifiers that refer to WorkflowEntrypoint, including
 * `import { WorkflowEntrypoint as Alias }` renames. Always include the bare
 * name so sources that reference it without a rewritten import still match.
 */
function workflowEntrypointAliases(source: string): Set<string> {
  const aliases = new Set<string>(["WorkflowEntrypoint"]);
  for (const importMatch of source.matchAll(
    /import\s*\{([^}]*)\}\s*from\s*["']cloudflare:workers["']/g
  )) {
    for (const segment of importMatch[1].split(",")) {
      const alias = segment
        .trim()
        .match(/^WorkflowEntrypoint(?:\s+as\s+([\w$]+))?$/);
      if (alias) aliases.add(alias[1] ?? "WorkflowEntrypoint");
    }
  }
  return aliases;
}

/**
 * Public export names for a local class binding. Mirrors DO detection so
 * `class X …; export { X as Y }` surfaces under the module export name `Y`.
 */
function exportedWorkflowNames(source: string, localName: string): string[] {
  const escaped = localName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const names = new Set<string>();
  if (
    new RegExp(
      String.raw`export\s+(?:default\s+)?class\s+${escaped}(?![\w$])`
    ).test(source)
  ) {
    names.add(localName);
  }
  for (const exportMatch of source.matchAll(/export\s*\{([^}]*)\}/g)) {
    for (const segment of exportMatch[1].split(",")) {
      const spec = segment.trim().match(/^([\w$]+)(?:\s+as\s+([\w$]+))?$/);
      if (spec && spec[1] === localName) names.add(spec[2] ?? localName);
    }
  }
  return [...names];
}

function workflowStepsInClassBody(
  source: string,
  body: string,
  bodyOffset: number
): WorkflowSourceStep[] {
  const stepPattern =
    /\b(?:step|[A-Za-z_$][\w$]*)\s*\.\s*(do|sleep|sleepUntil|waitForEvent)\s*\(\s*(["'`])([^"'`$]+)\2/g;
  const ranges = parallelRanges(body);
  const steps: WorkflowSourceStep[] = [];
  for (const [index, stepMatch] of [...body.matchAll(stepPattern)].entries()) {
    const localIndex = stepMatch.index ?? 0;
    const group = ranges.find(
      ([start, end]) => localIndex >= start && localIndex <= end
    );
    const kind = stepMatch[1] as WorkflowStepKind;
    const name = stepMatch[3];
    steps.push({
      id: `${kind}:${name}:${index + 1}`,
      kind,
      name,
      line: source.slice(0, bodyOffset + localIndex).split("\n").length,
      ...(group ? { parallelGroup: group[2] } : {})
    });
  }
  return steps;
}

export function detectWorkflowDefinitions(
  files: Record<string, string>
): DetectedWorkflowDefinition[] {
  const definitions: DetectedWorkflowDefinition[] = [];

  for (const [file, source] of Object.entries(files)) {
    const aliases = [...workflowEntrypointAliases(source)]
      .map((name) => name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))
      .join("|");
    // `export` is optional so `class X …; export { X }` is seen, and the alias set covers
    // both `import { WorkflowEntrypoint as WE }` and `Namespace.WorkflowEntrypoint`.
    const classPattern = new RegExp(
      String.raw`(?:export\s+(?:default\s+)?)?class\s+([A-Za-z_$][\w$]*)\s+extends\s+(?:[A-Za-z_$][\w$]*\.)?(?:${aliases})\b`,
      "g"
    );
    for (const classMatch of source.matchAll(classPattern)) {
      const localName = classMatch[1];
      const open = source.indexOf("{", classMatch.index ?? 0);
      if (open < 0) continue;
      const close = matchingBrace(source, open);
      const body = source.slice(open + 1, close);
      const bodyOffset = open + 1;
      const steps = workflowStepsInClassBody(source, body, bodyOffset);
      for (const className of exportedWorkflowNames(source, localName)) {
        definitions.push({ className, file, steps });
      }
    }
  }
  return definitions;
}

export const WORKFLOW_TRACKING_RUNTIME_SOURCE = String.raw`
function __trackWorkflowStep(gateway, className, instanceId, step, definitions) {
  if (!gateway || !instanceId || !step) return step;
  const methods = new Set(["do", "sleep", "sleepUntil", "waitForEvent"]);
  return new Proxy(step, {
    get(target, property, receiver) {
      const original = Reflect.get(target, property, receiver);
      if (!methods.has(property) || typeof original !== "function") {
        return original;
      }
      return async (name, ...args) => {
        const definition = definitions.find((entry) =>
          entry.kind === property && entry.name === String(name));
        let token;
        try {
          token = await gateway.startStep(
            String(instanceId),
            className,
            property,
            String(name),
            definition?.line,
            definition?.parallelGroup
          );
        } catch {}
        try {
          // Workflow step methods are RPC function capabilities. Accessing
          // Function.prototype.call on one asks the remote receiver for a
          // method literally named "call", so invoke the capability directly.
          const result = await original(name, ...args);
          if (token) {
            try { await gateway.finishStep(String(instanceId), token, "complete", result); } catch {}
          }
          return result;
        } catch (error) {
          if (token) {
            try { await gateway.finishStep(String(instanceId), token, "errored", error); } catch {}
          }
          throw error;
        }
      };
    }
  });
}
`;
